import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton, TertiaryButton } from "../components/Button";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { getUserProfile } from "../services/api";
import { openSupportWhatsApp } from "../constants/support";

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const response = await getUserProfile(user.id);
        setUserProfile(response.user);
      } catch (error) {
        console.error("Error loading user profile:", error);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  // Show loading state while profile is loading
  if (loading) {
    return (
      <div className="h-[100dvh] w-full bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading. Please wait.</p>
        </div>
      </div>
    );
  }

  // Show error state if profile loading failed
  if (error) {
    return (
      <div className="h-[100dvh] w-full bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-center px-5 space-y-3">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#D62422] text-white rounded"
            >
              Retry
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded"
            >
              Back to Role Selection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // single viewport scroller + 400px shell
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto">
      <div className="mx-auto w-full min-h-[100dvh] flex flex-col">

        {/* Header (gradient) */}
        <header className="relative w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)]">
          {/* NAVBAR INSIDE HEADER */}
          <Navbar
            onBack={() => navigate("/student/home")}
            background="transparent"
          // spacerHeight={40}
          />

          <div className="px-5 pb-6">
            {/* avatar + name */}
            <div className="mt-6 flex flex-col items-center">
              <div className="w-16 h-16 grid place-items-center bg-[#252525] border border-[#727272]">
                {/* user icon inside square */}
                <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
                  <path d="M18 21C11 21 7.5 24 7.5 24V28.5H28.5V24C28.5 24 25 21 18 21Z" stroke="#B0B0B0" strokeWidth="2" />
                  <path d="M23 12.5C23 15.26 20.76 17.5 18 17.5C15.24 17.5 13 15.26 13 12.5C13 9.74 15.24 7.5 18 7.5C20.76 7.5 23 9.74 23 12.5Z" stroke="#B0B0B0" strokeWidth="2" />
                </svg>
              </div>

              <div className="mt-3 text-white text-[20px] font-bold">
                {loading ? "Loading..." : userProfile?.name || "User"}
              </div>
              <div className="mt-1 text-[#B0B0B0] text-[12px]">
                {userProfile?.countryCode && userProfile?.mobileNumber
                  ? `+${userProfile.countryCode} ${userProfile.mobileNumber}`
                  : "Phone number not available"
                }
              </div>
            </div>

            {/* CTA button */}
            <div className="mt-5">
              <PrimaryButton
                label="EDIT PROFILE & GEARS"
                onClick={() => navigate("/edit-profile")}
                className="!w-full"
              />
            </div>
          </div>
        </header>

        {/* Settings */}
        <main className="px-5 py-6">
          <div className="text-[12px] font-extrabold tracking-[0.05em] text-[#B0B0B0] uppercase mb-4">
            // Settings
          </div>

          <div className="space-y-4">
            <TertiaryButton
              label="SAVED ADDRESS"
              layout="split"
              onClick={() => navigate("/saved-addresses")}
              className="!w-full"
            />
            <TertiaryButton
              label="CONTACT SUPPORT"
              layout="split"
              onClick={openSupportWhatsApp}
              className="!w-full"
            />
            <TertiaryButton
              label="LOGOUT"
              layout="split"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="!w-full"
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfileScreen;
